import { GridColDef } from "@mui/x-data-grid";
import { usePrestacaoContasDispatch, usePrestacaoContasState } from "./prestacao-contas-context";
import { ACTION_TYPES, initialDataRequest } from "./prestacao-contas-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IPrestacaoContasResponse } from "../../../../model/admin-portal/response/prestacao-contas-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { IDocumentoPrestacaoContasRequest } from "../../../../model/admin-portal/request/documento-prestacao-contas-request";

// Formata o período de forma legível conforme a periodicidade do tipo de documento selecionado
// (2=Bimestral, 3=Quadrimestral mostram o número do período; 1=Anual e 4=Quadrienal mostram só o(s) ano(s)).
export const formatPeriodo = (indPeriodicidade: number, ano: number, periodo?: number | null, anoFim?: number | null): string => {
    switch (indPeriodicidade) {
        case 2: return periodo ? `${periodo}º Bimestre/${ano}` : `${ano}`;
        case 3: return periodo ? `${periodo}º Quadrimestre/${ano}` : `${ano}`;
        case 4: return anoFim ? `${ano}-${anoFim}` : `${ano}`;
        case 5: return periodo ? `${periodo}º Trimestre/${ano}` : `${ano}`;
        default: return `${ano}`;
    }
};

export const usePrestacaoContasController = () => {
    const state = usePrestacaoContasState();
    const dispatch = usePrestacaoContasDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listPrestacoesContas(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar prestações de contas' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const tipos = await AdminPortalAppService.listTiposDocumentoPrestacaoContas();
            dispatch({ type: ACTION_TYPES.setTiposList, payload: tipos });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar lista de Tipos de Documento' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getPrestacaoContas(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idTipoDocumentoPrestacaoContas: response.idTipoDocumentoPrestacaoContas,
                    ano: response.ano,
                    anoFim: response.anoFim ?? null,
                    periodo: response.periodo ?? null,
                    textoDescricao: response.textoDescricao,
                    indStatus: response.indStatus,
                    dtaPublicacao: response.dtaPublicacao,
                    documentos: response.documentos?.map(d => ({ id: d.id, descricao: d.descricao, dtaPublicacao: d.dtaPublicacao, documentoUrl: d.documentoUrl })) || []
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar prestação de contas' });
        } finally {
            showBackDrop(false);
        }
    };

    const getTipoSelecionado = () => state.tiposList?.find(t => t.id === state.dataRequest?.idTipoDocumentoPrestacaoContas);

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idTipoDocumentoPrestacaoContas) return false;
        if (!r.ano || r.ano <= 0) return false;
        if (!r.textoDescricao || r.textoDescricao.trim() === '') return false;
        if (r.indStatus === null || r.indStatus === undefined) return false;
        if (!r.dtaPublicacao) return false;

        const tipo = getTipoSelecionado();
        if (tipo && (tipo.indPeriodicidade === 2 || tipo.indPeriodicidade === 3 || tipo.indPeriodicidade === 5) && !r.periodo) return false;
        if (tipo && tipo.indPeriodicidade === 4 && (!r.anoFim || r.anoFim < r.ano)) return false;

        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        let updated = { ...state.dataRequest, [field]: value };

        // Trocar o tipo de documento pode mudar a periodicidade - zera o período selecionado
        // para não deixar um valor de bimestre/quadrimestre inconsistente com o novo tipo.
        if (field === 'idTipoDocumentoPrestacaoContas') {
            updated.periodo = null;
            updated.anoFim = null;
        }

        dispatch({ type: ACTION_TYPES.setDataRequest, payload: updated });
    };

    const openForm = (row?: IPrestacaoContasResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdatePrestacaoContas(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Prestação de contas atualizada com sucesso' : 'Prestação de contas criada com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar prestação de contas' });
        } finally {
            showBackDrop(false);
        }
    };

    //Cada filho é mantido só no estado local até o "Salvar" da Prestação de Contas inteira - linhas novas usam id 0
    const saveDocumento = (doc: IDocumentoPrestacaoContasRequest, index?: number) => {
        const documentos = [...state.dataRequest.documentos];
        if (index !== undefined) documentos[index] = doc; else documentos.push(doc);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const removeDocumento = (index: number) => {
        const documentos = state.dataRequest.documentos.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'tipoDocumentoPrestacaoContasName', headerName: 'Tipo de Documento', flex: 1.4, minWidth: 220 },
            {
                field: 'periodo', headerName: 'Período', width: 150,
                valueGetter: (_value, row) => formatPeriodo(row.indPeriodicidade, row.ano, row.periodo, row.anoFim)
            },
            {
                field: 'indStatus', headerName: 'Status', width: 130,
                renderCell: (params) => (
                    <Chip
                        label={params.value === 2 ? 'Finalizado' : 'Em Execução'}
                        color={params.value === 2 ? 'success' : 'warning'}
                        size="small"
                    />
                )
            },
            { field: 'textoDescricao', headerName: 'Descrição', flex: 1.6, minWidth: 220 },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        getTipoSelecionado,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid,
        saveDocumento,
        removeDocumento
    };
};
